import * as Accordion from "#root/components/accordion";
import { Case, Switch } from "#root/components/conditionals";
import * as Fields from "#root/components/form/fields";
import { GlyphLoading } from "#root/components/glyphs";
import Sidebar from "#root/components/sidebar";
import StandardEditor, { type StandardEditorProps } from "#root/components/standard-editor";
import { WithSidebar } from "#root/components/withSidebar";
import { productCollection, type ProductDetailItem, type ProductDetailSchema, type ProductListItem, type ProductListSchema } from "#root/schema/product";
import { vendorCollection } from "#root/schema/vendor";
import { useData } from "#root/utility/hooks";
import { leftJoin } from "#root/utility/joins";
import { mergeUpdate } from "#root/utility/merge-update";
import type { RecordModificationResponse } from "#root/utility/remote-data";
import { flatToNamedTree } from "#root/utility/tree";
import type { ReactNode } from "react";
import { useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageLayout from "./layout";

export default function ({ id }: { id: string }): ReactNode {
  const navigate = useNavigate();
  const [products, setProducts] = useData(async () => {
    const { rows } = await productCollection.list();
    return rows;
  }, []);

  const [vendors] = useData(async () => {
    const { rows } = await vendorCollection.list();
    return rows;
  }, []);

  useMemo(
    () =>
      leftJoin({
        left: products,
        right: vendors,
        leftKey: "vendorId",
        rightKey: "id",
        leftDest: "vendor",
      }),
    [products, vendors],
  );

  const handleUpdate = useCallback(
    (data: RecordModificationResponse<ProductDetailItem>) => {
      setProducts(mergeUpdate(products!, data));

      if ("deleted" in data) {
        navigate("/admin/product");
      } else if ("created" in data) {
        navigate(`/admin/product/${data.created}`);
      }
    },
    [navigate, setProducts, products],
  );

  if (!products || !vendors) {
    return (
      <PageLayout title="Product">
        <section>
          <GlyphLoading/>
        </section>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Admin - Product">
      <WithSidebar>
        <ProductSidebar products={products} id={id}/>
        <section>
          <Switch>
            <Case expr={id === "new"}>
              <h1>New Product</h1>
              <ProductEditor collection={productCollection} onSave={handleUpdate}/>
            </Case>
            <Case expr={!!id}>
              <h1>Edit Product</h1>
              <ProductEditor id={id} collection={productCollection} onSave={handleUpdate} onDelete={handleUpdate}/>
            </Case>
          </Switch>
        </section>
      </WithSidebar>
    </PageLayout>
  );
}

function ProductSidebar({ products, id }: { products: ProductListItem[]; id: string | null }) {
  const navigate = useNavigate();
  const itemId = useMemo(() => {
    for (const product of products) {
      if (String(product.id) === id || product.code === id) {
        return product.id;
      }
    }
  }, [products, id]);

  const categories = useMemo(() => flatToNamedTree(products, p => p.category, p => p.name), [products]);

  return (
    <Sidebar>
      <button className="flat center" type="button" onClick={() => navigate("/admin/product/new")}>
        New Product
      </button>
      <Accordion.Parent initialItemId={itemId}>
        {categories.map(({ name, items }) => (
          <Accordion.Group key={name} label={name}>
            {items.map(item => (
              <Accordion.Item key={item.id} itemId={item.id}>
                <Link to={"/admin/product/" + item.id}>{item.name}</Link>
              </Accordion.Item>
            ))}
          </Accordion.Group>
        ))}
      </Accordion.Parent>
    </Sidebar>
  );
}

function ProductEditor(props: StandardEditorProps<typeof ProductListSchema, typeof ProductDetailSchema>) {
  return (
    <StandardEditor {...props} name="Product">
      <Fields.Set name="name">
        <label>Product Name</label>
        <Fields.ProductName required/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="code">
        <label>Product Code</label>
        <Fields.ObjectCode required/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="type">
        <label>Product Type</label>
        <Fields.Select required>
          <Fields.Option value="API">API</Fields.Option>
          <Fields.Option value="VISUALIZATION">VISUALIZATION</Fields.Option>
          <Fields.Option value="DATASET">DATASET</Fields.Option>
          <Fields.Option value="SERVICE">SERVICE</Fields.Option>
          <Fields.Option value="INTERNAL">INTERNAL</Fields.Option>
        </Fields.Select>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="vendorId">
        <label>Vendor Name</label>
        <Fields.AutoComplete required collection={vendorCollection}/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="description">
        <label>Product Description</label>
        <Fields.TextArea rows={3}/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="page">
        <label>Product Page</label>
        <Fields.Markdown rows={10}/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="defaultEntitlementOptions">
        <label>Default Entitlement Options</label>
        <Fields.JSONEditor rows={1}/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="category">
        <label>Category</label>
        <Fields.CategoryName/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="provides">
        <label>Provides Feature</label>
        <Fields.Input/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="revenueAccount">
        <label>Quickbooks Revenue Account</label>
        <Fields.Input/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="tags">
        <label>Tags [JSON Format]</label>
        <Fields.Tags/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="listed">
        <label>Listing Status</label>
        <Fields.BooleanSelect>
          <Fields.Option value="0">Not Listed</Fields.Option>
          <Fields.Option value="1">Listed</Fields.Option>
        </Fields.BooleanSelect>
        <Fields.Validity/>
      </Fields.Set>
    </StandardEditor>
  );
}
